import PageTitle from "@app/components/page-title.tsx";
import useUserData from "@app/data-hooks/use-user-data.ts";
import { Box, Button, Stack } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { Delimiter, PasswordField } from "bi-lib";

interface FormProps {
    oldPassword: string;
    newPassword: string;
    newPasswordConfirm: string;
}

export const Route = createFileRoute("/_authenticated/account/change-password")({
    component: Component,
});

function Component() {
    const { changePassword } = useUserData();

    const form = useForm({
        defaultValues: {} as FormProps,
        onSubmit: ({ value }) => {
            changePassword.mutate({ old_password: value.oldPassword, new_password: value.newPassword });
        },
    });

    return (
        <>
            <PageTitle>Change Password</PageTitle>
            <Box display={"flex"} justifyContent={"center"}>
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        void form.handleSubmit();
                    }}
                    style={{ width: "100%", maxWidth: 500 }}
                >
                    <Stack spacing={1}>
                        <form.Field name={"oldPassword"}>
                            {(field) => (
                                <PasswordField
                                    label={"Old Password"}
                                    value={field.state.value}
                                    onChange={field.handleChange}
                                    required
                                />
                            )}
                        </form.Field>
                        <form.Field
                            name={"newPassword"}
                            validators={{
                                onChange: ({ value }) => {
                                    const errors: string[] = [];

                                    // Check length
                                    const minLength = 8;
                                    if (value.length < minLength) {
                                        errors.push(`Must be at least ${minLength} characters`);
                                    }

                                    // Check for lowercase
                                    if (!/[a-z]/.test(value)) {
                                        errors.push("Must contain at least 1 lowercase character");
                                    }

                                    // Check for uppercase
                                    if (!/[A-Z]/.test(value)) {
                                        errors.push("Must contain at least 1 uppercase character");
                                    }

                                    // Check for number
                                    if (!/[0-9]/.test(value)) {
                                        errors.push("Must contain at least 1 number");
                                    }

                                    // Check for number
                                    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                                        errors.push("Must contain at least 1 symbol");
                                    }

                                    return errors.length == 0 ? undefined : errors.join(Delimiter);
                                },
                            }}
                        >
                            {(field) => (
                                <PasswordField
                                    label={"New Password"}
                                    value={field.state.value}
                                    onChange={field.handleChange}
                                    required
                                    error={field.state.meta.errors.length > 0}
                                    helperText={field.state.meta.errors.join(Delimiter)}
                                />
                            )}
                        </form.Field>
                        <form.Field
                            name={"newPasswordConfirm"}
                            validators={{
                                onChangeListenTo: ["newPassword"],
                                onChange: ({ value, fieldApi }) => {
                                    if (value !== fieldApi.form.getFieldValue("newPassword")) {
                                        return "Passwords do not match";
                                    }
                                    return undefined;
                                },
                            }}
                        >
                            {(field) => (
                                <PasswordField
                                    label={"Confirm New Password"}
                                    value={field.state.value}
                                    onChange={field.handleChange}
                                    required
                                    error={field.state.meta.errors.length > 0}
                                    helperText={field.state.meta.errors.join(Delimiter)}
                                />
                            )}
                        </form.Field>
                        <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                            {([canSubmit]) => (
                                <Button
                                    variant={"contained"}
                                    type={"submit"}
                                    disabled={!canSubmit || changePassword.isPending}
                                >
                                    Save
                                </Button>
                            )}
                        </form.Subscribe>
                    </Stack>
                </form>
            </Box>
        </>
    );
}
